import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import HomePage from './pages/HomePage';
import GamesPage from './pages/GamesPage';
import AboutPage from './pages/AboutPage';
import NotFoundPage from './pages/NotFoundPage';
import { useI18n } from './hooks/useI18n';
import { useAssetPreloader } from './hooks/useAssetPreloader';
import { LoadingScreen } from './components/LoadingScreen/LoadingScreen';
import { StyledBackTop, FABButton, FABIcon } from './App.styles';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

const MainContent: React.FC = React.memo(() => {
  const location = useLocation();
  const isGamesPage = useMemo(() => location.pathname === '/games', [location.pathname]);
  const isHomePage = useMemo(() => location.pathname === '/', [location.pathname]);

  return (
    <main
      className={`main-content ${isGamesPage ? 'games-page' : ''} ${isHomePage ? 'home-page' : ''}`}
    >
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/games" element={<GamesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </main>
  );
});

const App = (): React.JSX.Element => {
  const { t, language } = useI18n();
  const { isLoading } = useAssetPreloader();
  const [showMainContent, setShowMainContent] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowMainContent(true);
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  useEffect(() => {
    const metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (metaDescription) {
      metaDescription.content = t('meta.description');
    }
  }, [language, t]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="app-container">
        {!showMainContent && <LoadingScreen isLoading={isLoading} />}

        {showMainContent && (
          <div className="App fade-in">
            <Header />
            <MainContent />
            <Footer />
          </div>
        )}

        <StyledBackTop visibilityHeight={400}>
          <FABButton>
            <FABIcon />
          </FABButton>
        </StyledBackTop>
      </div>
    </BrowserRouter>
  );
};

export default App;
